import type { QuizAttemptSummary } from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { Button } from "@/components/ui/button"
import { Badge, Flex, Heading, Stack, Table, Text } from "@chakra-ui/react"
import { Link } from "@tanstack/react-router"

interface AttemptsListProps {
  attempts: QuizAttemptSummary[]
}

export function AttemptsList({ attempts }: AttemptsListProps) {
  if (attempts.length === 0) {
    return (
      <AppEmptyState
        title={emptyStateCopy.attempts.none.title}
        description={emptyStateCopy.attempts.none.description}
        data-testid="attempts-empty-state"
      />
    )
  }

  return (
    <Stack gap={6}>
      <Table.Root display={{ base: "none", md: "table" }}>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader minW="320px">Quiz</Table.ColumnHeader>
            <Table.ColumnHeader>Score</Table.ColumnHeader>
            <Table.ColumnHeader>Date</Table.ColumnHeader>
            <Table.ColumnHeader>Actions</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {attempts.map((attempt) => {
            const resultsHidden = attempt.completed && attempt.score === null
            const scoreLabel = !attempt.completed
              ? "Incomplete"
              : resultsHidden
                ? "Results Scheduled"
                : `${attempt.score} / ${attempt.total_questions}`
            const subjects = attempt.subjects ?? []
            const tags = attempt.tags ?? []

            return (
              <Table.Row key={attempt.attempt_id}>
                <Table.Cell minW="320px">
                  <Text>{attempt.quiz_name}</Text>
                  <Flex gap={2} wrap="wrap" mt={2}>
                    {subjects.map((subject) => (
                      <Badge key={subject.id} colorPalette="purple">
                        {subject.name}
                      </Badge>
                    ))}
                    {tags.map((tag) => (
                      <Badge key={tag} colorPalette="blue">
                        {tag}
                      </Badge>
                    ))}
                  </Flex>
                </Table.Cell>
                <Table.Cell>
                  {resultsHidden || !attempt.completed ? (
                    <Badge colorPalette="gray">{scoreLabel}</Badge>
                  ) : (
                    scoreLabel
                  )}
                </Table.Cell>
                <Table.Cell>
                  {attempt.completed_at || attempt.started_at
                    ? new Date(
                        attempt.completed_at ?? attempt.started_at!,
                      ).toLocaleDateString()
                    : "N/A"}
                </Table.Cell>
                <Table.Cell>
                  {attempt.can_review ? (
                    <Button asChild size="sm">
                      <Link
                        to="/quizzes/$quizId/attempts/$attemptId/review"
                        params={{
                          quizId: attempt.quiz_id,
                          attemptId: attempt.attempt_id,
                        }}
                      >
                        Review
                      </Link>
                    </Button>
                  ) : (
                    <Badge colorPalette="gray">
                      {resultsHidden ? "Pending" : "No Review"}
                    </Badge>
                  )}
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>

      <Stack gap={4} display={{ base: "flex", md: "none" }}>
        {attempts.map((attempt) => {
          const resultsHidden = attempt.completed && attempt.score === null
          const scoreLabel = !attempt.completed
            ? "Incomplete"
            : resultsHidden
              ? "Results scheduled"
              : `${attempt.score} / ${attempt.total_questions}`
          const subjects = attempt.subjects ?? []
          const tags = attempt.tags ?? []

          return (
            <Stack
              key={attempt.attempt_id}
              borderWidth="1px"
              borderRadius="lg"
              p={4}
              shadow="sm"
              bg="white"
              gap={2}
            >
              <Heading size="md">{attempt.quiz_name}</Heading>

              <Flex gap={2} wrap="wrap">
                {subjects.map((subject) => (
                  <Badge key={subject.id} colorPalette="purple">
                    {subject.name}
                  </Badge>
                ))}
                {tags.map((tag) => (
                  <Badge key={tag} colorPalette="blue">
                    {tag}
                  </Badge>
                ))}
              </Flex>

              <Stack gap={1} fontSize="sm">
                <Text>
                  <strong>Score:</strong> {scoreLabel}
                </Text>
                <Text>
                  <strong>Date:</strong>{" "}
                  {attempt.completed_at || attempt.started_at
                    ? new Date(
                        attempt.completed_at ?? attempt.started_at!,
                      ).toLocaleDateString()
                    : "N/A"}
                </Text>
              </Stack>

              {attempt.can_review ? (
                <Button asChild size="sm" alignSelf="flex-start">
                  <Link
                    to="/quizzes/$quizId/attempts/$attemptId/review"
                    params={{
                      quizId: attempt.quiz_id,
                      attemptId: attempt.attempt_id,
                    }}
                  >
                    Review
                  </Link>
                </Button>
              ) : (
                <Badge colorPalette="gray" alignSelf="flex-start">
                  {resultsHidden ? "Pending" : "No Review"}
                </Badge>
              )}
            </Stack>
          )
        })}
      </Stack>
    </Stack>
  )
}
